import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAcl } from '@/hooks/useAcl';
import { useToast } from '@/components/base/Toast';
import { ALL_FEATURES, defaultAcl } from '@/mocks/aclPermissions';
import { getRoleHomeRoute } from '@/router/roleRoutes';
import { userProfiles, getInitials } from '@/mocks/userProfiles';
import { useCurrentUser } from '@/hooks/useCurrentUser';

const ALL_ROLES = Object.keys(defaultAcl);

const avatarColors: string[] = [
  'bg-accent-500',
  'bg-primary-500',
  'bg-secondary-500',
  'bg-amber-500',
  'bg-emerald-500',
  'bg-rose-500',
  'bg-teal-500',
  'bg-orange-500',
  'bg-cyan-500',
  'bg-violet-500',
];

function hashRoleToColor(role: string): string {
  let hash = 0;
  for (let i = 0; i < role.length; i++) {
    hash = role.charCodeAt(i) + ((hash << 5) - hash);
  }
  return avatarColors[Math.abs(hash) % avatarColors.length];
}

export default function ProfileDropdown() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { role, canView, setRole } = useAcl();
  const toast = useToast();
  const [open, setOpen] = useState(false);

  const { name: displayName, email, initials } = useCurrentUser();
  const avatarColor = hashRoleToColor(role);

  const effectiveFeatures = ALL_FEATURES.filter((f) => canView(f.feature));

  const handleRoleSwitch = (newRole: string) => {
    setRole(newRole);
    toast.success(t('common.roleSwitched', { role: newRole }));
    setOpen(false);
    // The current page may belong to the previous role, so follow the switch
    // to the new role's dashboard instead of leaving a stale view on screen.
    navigate(getRoleHomeRoute(newRole));
  };

  const handleSignOut = () => {
    setOpen(false);
    setRole('Super Admin');
    navigate('/login');
  };

  return (
    <div className="relative">
      <button
        aria-label={t('common.profile')}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="w-9 h-9 rounded-full flex items-center justify-center text-background-50 text-xs font-label font-700 cursor-pointer hover:ring-2 hover:ring-background-300 transition-all"
      >
        <span className={`w-9 h-9 rounded-full flex items-center justify-center text-background-50 text-xs font-label font-700 ${avatarColor}`}>
          {initials}
        </span>
      </button>

      {open && (
        <>
          <button
            className="fixed inset-0 z-30 cursor-default"
            onClick={() => setOpen(false)}
            aria-label="Close profile menu"
            tabIndex={-1}
          />
          <div className="absolute right-0 top-12 w-80 bg-background-50 rounded-xl shadow-floating border border-background-200 overflow-hidden z-40">
            {/* Header — user info */}
            <div className="px-4 py-4 border-b border-background-200">
              <div className="flex items-center gap-3">
                <span className={`w-10 h-10 rounded-full flex items-center justify-center text-background-50 text-sm font-label font-700 flex-shrink-0 ${avatarColor}`}>
                  {initials}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground-900 font-label truncate">{displayName}</p>
                  <p className="text-xs text-foreground-500 font-label truncate">{email}</p>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent-100 text-accent-800 text-xs font-label font-medium">
                  <i className="ri-shield-user-line text-xs" />
                  {role}
                </span>
              </div>
            </div>

            {/* Switch Role */}
            <div className="px-4 py-3 border-b border-background-200">
              <p className="text-xs font-label font-semibold text-foreground-500 uppercase tracking-wide mb-2.5">
                <i className="ri-arrow-left-right-line text-xs mr-1" />
                {t('common.switchRole')}
              </p>
              <div className="grid grid-cols-2 gap-1.5 max-h-44 overflow-y-auto">
                {ALL_ROLES.map((roleName) => {
                  const profileForRole = userProfiles[roleName];
                  const roleInitials = getInitials(profileForRole?.name ?? roleName);
                  const roleColor = hashRoleToColor(roleName);
                  const isActive = role === roleName;

                  return (
                    <button
                      key={roleName}
                      onClick={() => handleRoleSwitch(roleName)}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-left transition-colors cursor-pointer whitespace-nowrap ${
                        isActive
                          ? 'bg-accent-100 border border-accent-300'
                          : 'bg-background-100 border border-transparent hover:bg-background-200 hover:border-background-300'
                      }`}
                    >
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-background-50 text-[9px] font-label font-700 flex-shrink-0 ${roleColor}`}>
                        {roleInitials}
                      </span>
                      <span className={`text-xs font-label truncate ${isActive ? 'text-accent-800 font-medium' : 'text-foreground-700'}`}>
                        {roleName}
                      </span>
                      {isActive && (
                        <i className="ri-check-line text-accent-600 text-xs ml-auto flex-shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Permissions summary */}
            <div className="px-4 py-3 border-b border-background-200">
              <p className="text-xs font-label font-semibold text-foreground-500 uppercase tracking-wide mb-2.5">
                {t('common.effectivePermissions')}
                <span className="ml-1 font-normal text-foreground-400">({effectiveFeatures.length})</span>
              </p>
              <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
                {effectiveFeatures.map((f) => (
                  <span
                    key={f.feature}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-background-100 text-foreground-600 text-xs font-label cursor-default"
                  >
                    <i className={`${f.icon} text-xs text-foreground-400`} />
                    {t(f.labelKey)}
                  </span>
                ))}
                {effectiveFeatures.length === 0 && (
                  <p className="text-xs text-foreground-400 font-label italic">{t('common.noPermissions')}</p>
                )}
              </div>
            </div>

            {/* Sign out */}
            <div className="px-3 py-2">
              <button
                onClick={handleSignOut}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-label text-foreground-600 hover:bg-rose-50 hover:text-rose-600 transition-colors cursor-pointer"
              >
                <i className="ri-logout-box-r-line text-base" />
                {t('common.signOut')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}